import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageMap } from '@ngx-pwa/local-storage';
import { BehaviorSubject, Observable } from 'rxjs';
import { last, map, switchMap, tap } from 'rxjs/operators';

import { getUsedDiscreteVals, IDataset, offsetData } from '../models/dataset';
import { DatasetType } from '../models/dataset-type';
import { Metadata } from '../models/metadata';

import { LoadingProgressService, LoadingProgressTracker } from './http-progress';
import { Latis } from './latis.service';

interface IPlotDatasets {
    datasets: IDataset[];
    loading: boolean; // set if datasets have not yet downloaded their data
    error?: string; // errors returned by the http calls
}

@Injectable({ providedIn: 'root' })
export class DatasetService {
    plotDatasetsMap: Map<string, BehaviorSubject<IPlotDatasets>> = new Map();

    constructor(
        private _latis: Latis,
        private _http: HttpClient,
        private _loadingService: LoadingProgressService,
        private _storage: StorageMap
    ) {}

    /** add a new entry for a plot's datasets to the map */
    initPlotDatasets( plotId: string, datasets: IDataset[] = [] ): void {
        this.plotDatasetsMap.set( plotId, new BehaviorSubject({
            datasets: datasets,
            loading: true
        }));
    }

    /** return the observable that updates whenever we update this plots datasets */
    getPlotDatasets( plotId: string ): IPlotDatasets {
        return this.plotDatasetsMap.get( plotId ).getValue();
    }

    /** return the observable that updates whenever we update this plots datasets */
    getPlotDatasets$( plotId: string ): BehaviorSubject<IPlotDatasets> {
        return this.plotDatasetsMap.get( plotId );
    }

    /** set a new list of plot datasets, mark them to be loaded */
    setPlotDatasets( plotId: string, datasets: IDataset[] ): void {
        let loading = false;
        if ( datasets.length > 0 ) {
            this.downloadData( plotId, datasets );
            loading = true;
        }
        this.plotDatasetsMap.get( plotId ).next({
            datasets: datasets,
            loading: loading
        });
    }

    /**
     * Downloads data using the given parameters and URL for each datasets
     * Determines the download function to use based on plot type
     */
    downloadData( plotId: string, datasets: IDataset[] ) {
        datasets.forEach( ( dataset, index, dsArray ) => {
            // setup loading trackers
            const loadingTracker = new LoadingProgressTracker( dataset.range.start, dataset.range.end );
            this._loadingService.addNewTracker( plotId, loadingTracker );

            // download line data
            if ( dataset.type === DatasetType.analog || dataset.type === DatasetType.discrete ) {
                this.downloadOrRetrieveFromStorage( this.downloadLineData, dataset, loadingTracker, dsArray, index, plotId );
            }
            // download image data
            if ( dataset.type === DatasetType.image ) {
                this.downloadOrRetrieveFromStorage( this.downloadImageData, dataset, loadingTracker, dsArray, index, plotId );
            }
            // download text data
            if ( dataset.type === DatasetType.eventTable ) {
                this.downloadOrRetrieveFromStorage( this.downloadTextData, dataset, loadingTracker, dsArray, index, plotId );
            }
        });
    }

    /**
     * Check if a dataset is in storage, if not request the dataset from LATIS
     * TODO: implement LRU, 1hr expire time, and check for new samples from LATIS before using cache
     */
    downloadOrRetrieveFromStorage(
        download: ( dataset: IDataset, loadingTracker: LoadingProgressTracker ) => Observable<IDataset>,
        dataset: IDataset,
        loadingTracker: LoadingProgressTracker,
        datasetArray: IDataset[],
        index: number,
        plotId: string
    ): void {
        this._storage.has( dataset.fullURL ).pipe(
            switchMap( isInStorage => {
                if ( isInStorage ) {
                    return this._storage.get<IDataset>( dataset.fullURL ).pipe(
                        tap( () => loadingTracker.setFullProgress() ),
                        tap( () => this._loadingService.updateAllTrackersProgress() )
                    );
                } else {
                    // remember to preserve reference to 'this'
                    return download.call( this, dataset, loadingTracker ).pipe( tap(
                        newDataset => this._storage.set( dataset.fullURL, newDataset ).subscribe()
                    ));
                }
            })
        ).subscribe(
            ( newDataset: IDataset ) => {
                datasetArray[ index ] = newDataset;
                this.checkLoadingComplete( plotId );
            },
            error => {
                dataset.loaded = true;
                this.checkLoadingComplete( plotId, error );
            }
        );
    }

    /** download x/y data for use in a line chart */
    downloadLineData( dataset: IDataset, loadingTracker: LoadingProgressTracker ): Observable<IDataset> {
        return this._http.request<IDataset>( this._latis.get( dataset.fullURL ) ).pipe(
            tap( event => loadingTracker.onProgressHandler( event ) ),
            tap( () => this._loadingService.updateAllTrackersProgress() ),
            last(),
            map( response => {
                const data = JSON.parse( response['body'] );
                // should be 1 key in the top level of the returned object. This key is commonly the dataset identifier
                for ( const key in data ) {
                    if ( data.hasOwnProperty( key ) ) {
                        dataset.data = offsetData( data[ key ].data, dataset.indexes, dataset.offset as number );
                        if ( data[key].parameters ) {
                            dataset.indexes.y.name = data[ key ].parameters[ dataset.indexes.y.value ];
                            dataset.indexes.x.name = data[ key ].parameters[ dataset.indexes.x.value ];
                        }
                        // has metadata property and metadata is not empty
                        if (data[key].metadata && Object.keys(data[key].metadata).length > 0) {
                            dataset.metadata = new Metadata( dataset.indexes.y.name, dataset.indexes.x.name, data[key].metadata );
                        }

                        if ( dataset.type === DatasetType.discrete ) {
                            dataset.discreteValues = getUsedDiscreteVals( dataset.data, dataset.indexes );
                        }
                    } else {
                        console.error('Something is wrong. The object latis returned is not as expected');
                    }
                }
                dataset.loaded = true;
                return dataset;
            })
        );
    }

    /** downloads a list of images ( as urls ) */
    downloadImageData( dataset: IDataset, loadingTracker: LoadingProgressTracker ): Observable<IDataset> {
        return this._http.request<IDataset>( this._latis.get( dataset.fullURL ) ).pipe(
            tap( event => loadingTracker.onProgressHandler( event ) ),
            tap( () => this._loadingService.updateAllTrackersProgress() ),
            last(),
            map( response => {
                dataset.data = JSON.parse( response['body'] );
                dataset.loaded = true;
                return dataset;
            })
        );
    }

    /** download an events list dataset */
    downloadTextData( dataset: IDataset, loadingTracker: LoadingProgressTracker ): Observable<IDataset> {
        return this._http.request<IDataset>( this._latis.get( dataset.fullURL ) ).pipe(
            tap( event => loadingTracker.onProgressHandler( event ) ),
            tap( () => this._loadingService.updateAllTrackersProgress() ),
            last(),
            map( response => {
                const data = JSON.parse( response['body'] );
                for ( const key in data ) {
                    if ( data.hasOwnProperty( key ) ) {
                        dataset.data = data[ key ].data;
                        // has metadata property and metadata is not empty
                        if ( data[ key ].metadata && Object.keys( data[ key ].metadata ).length > 0) {
                            dataset.metadata = new Metadata( key, 'time', data[key].metadata );
                        }
                    } else {
                        console.error('Something is wrong. The object latis returned is not as expected');
                    }
                }
                dataset.loaded = true;
                return dataset;
            })
        );
    }

    /** checks if all datasets for a plot are done downloading. If they are, this will signal loading complete */
    checkLoadingComplete( plotId: string, error?: string ): void {
        const datasets = this.getPlotDatasets( plotId ).datasets;
        if ( datasets.every( dataset => dataset.loaded ) ) {
            this.plotDatasetsMap.get( plotId ).next({
                datasets: datasets,
                loading: false,
                error: error
            });
            this._loadingService.removeTracker( plotId );
        }
    }
}
